import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { League } from '../lib/types'
import NotifyToggle from './NotifyToggle'

interface PickStatus {
  member_id: string
  team_name: string
  display_name: string
  is_you: boolean
  slots_filled: number
  slots_required: number
  is_complete: boolean
}

/**
 * The Sunday-morning page.
 *
 * The number that matters here is "who hasn't picked", and it is deliberately
 * a count, not a lineup — the server returns how many slots someone has filled
 * and never which units filled them, so this works in a league that hides
 * picks until kickoff without leaking a thing.
 */
export default function Roster({
  league, weekId, isCommissioner, onChanged,
}: {
  league: League
  weekId: string | null
  isCommissioner: boolean
  onChanged: () => void
}) {
  const [rows, setRows] = useState<PickStatus[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [name, setName] = useState(league.name)
  const [deadline, setDeadline] = useState<number | ''>(league.join_deadline_week ?? '')
  const [saved, setSaved] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!weekId) return
    const { data, error } = await supabase.rpc('league_pick_status', {
      p_league: league.id,
      p_week: weekId,
    })
    if (error) return setErr(error.message)
    setRows((data ?? []) as PickStatus[])
  }, [league.id, weekId])

  useEffect(() => { void load() }, [load])

  const missing = rows.filter((r) => !r.is_complete)

  const act = async (label: string, fn: () => PromiseLike<{ error: unknown }>) => {
    setBusy(label); setErr(null)
    const { error } = await fn()
    setBusy(null)
    if (error) return setErr((error as { message: string }).message)
    await load()
    onChanged()
  }

  const remove = (r: PickStatus) => {
    if (!confirm(
      `Remove ${r.team_name} from ${league.name}? Their picks and results go with them.`,
    )) return
    void act(r.member_id, () =>
      supabase.rpc('remove_member', { p_league: league.id, p_member: r.member_id }))
  }

  const handOver = (r: PickStatus) => {
    if (!confirm(
      `Hand ${league.name} over to ${r.team_name}? You lose commissioner controls immediately.`,
    )) return
    void act(r.member_id, () =>
      supabase.rpc('transfer_commission', { p_league: league.id, p_member: r.member_id }))
  }

  const saveSettings = async () => {
    setBusy('settings'); setErr(null); setSaved(null)
    const { error } = await supabase
      .from('leagues')
      .update({
        name: name.trim() || league.name,
        join_deadline_week: deadline === '' ? null : Number(deadline),
      })
      .eq('id', league.id)
    setBusy(null)
    if (error) return setErr(error.message)
    setSaved('Saved.')
    onChanged()
  }

  return (
    <div className="stack">
      <section className="card">
        <div className="row between">
          <h2>This week</h2>
          <span className={missing.length ? 'pill warn' : 'pill good'}>
            {missing.length
              ? `${missing.length} of ${rows.length} still to pick`
              : 'Everyone is in'}
          </span>
        </div>

        {!weekId && <p className="muted">Pick a week above.</p>}

        <ul className="roster">
          {rows.map((r) => (
            <li key={r.member_id} className={r.is_complete ? 'is-done' : 'is-waiting'}>
              <span className="dot" aria-hidden />
              <span className="who">
                {r.team_name}
                {r.is_you && <em className="you"> you</em>}
                <small className="muted"> {r.display_name}</small>
              </span>
              <span className="filled">
                {r.is_complete
                  ? 'Lineup set'
                  : `${r.slots_filled}/${r.slots_required} filled`}
              </span>
              {isCommissioner && !r.is_you && (
                <span className="row gap tight">
                  <button className="btn tiny" disabled={busy === r.member_id}
                          onClick={() => handOver(r)}>Hand over</button>
                  <button className="btn tiny danger" disabled={busy === r.member_id}
                          onClick={() => remove(r)}>Remove</button>
                </span>
              )}
            </li>
          ))}
        </ul>

        {err && <p className="error">{err}</p>}
      </section>

      <NotifyToggle />

      <section className="card">
        <h2>Invite</h2>
        <p className="muted">Anyone with the code can join.</p>
        <div className="row gap">
          <code className="code big">{league.join_code}</code>
          <button className="btn" onClick={() => {
            void navigator.clipboard.writeText(
              `${location.origin}/app/join?code=${league.join_code}`)
            setSaved('Invite link copied.')
          }}>Copy invite link</button>
        </div>
      </section>

      {isCommissioner && (
        <section className="card">
          <h2>League settings</h2>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label>
            Close joins after
            <select value={deadline}
                    onChange={(e) => setDeadline(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">Never — anyone can join any week</option>
              {Array.from({ length: 18 }, (_, i) => i + 1)
                .filter((w) => w >= league.start_week && w <= league.end_week)
                .map((w) => <option key={w} value={w}>Week {w}</option>)}
            </select>
          </label>
          <p className="window-fine">
            A manager who joins in week 8 arrives with all 192 units unburned while
            everyone else has spent forty-something. That is not a head start, it is a
            different game — they can take the best room available every week for the
            rest of the season. Closing joins two or three weeks in keeps the board
            honest.
          </p>

          <button className="btn btn-primary" disabled={busy === 'settings'}
                  onClick={saveSettings}>
            {busy === 'settings' ? 'Saving…' : 'Save'}
          </button>
          {saved && <p className="ok">{saved}</p>}
        </section>
      )}
    </div>
  )
}
